// ChatPanel — wired to POST /chat (M11).
//
// The panel renders four message kinds: system, user, assistant, and inline
// tool events (tool_call + tool_result). The user types; we POST the full
// history to /chat and render NDJSON events as they arrive. After
// chat_complete fires we call onRefresh so any newly queued runs appear in
// the board within milliseconds (the /runs poll is 5s).

import { useEffect, useRef, useState } from 'react';
import * as api from '../lib/api';
import { ApiError } from '../lib/api';
import type { ChatMessage as WireChatMessage, ChatStreamEvent, RunVM } from '../lib/types';
import { ChatPreferencesModal } from './ChatPreferencesModal';

type Message =
  | { kind: 'system'; text: string }
  | { kind: 'user'; text: string }
  | { kind: 'assistant'; text: string; runIdHint?: string }
  | { kind: 'tool_call'; tool: string; args: unknown; runIdHint?: string; pending: true }
  | {
      kind: 'tool_result';
      tool: string;
      result: unknown;
      error?: string;
      runIdHint?: string;
    }
  | { kind: 'error'; text: string };

interface ChatPanelProps {
  runs: RunVM[];
  collapsed: boolean;
  onToggleCollapsed: () => void;
  onJumpToCard: (runId: string) => void;
  /** Called after chat_complete so the board picks up any new runs without
   *  waiting for the next /runs poll. */
  onRefresh: () => Promise<void>;
  /** Fires the first time the operator sends a message in this session.
   *  Used by the parent to auto-collapse PromptComposer — the chat being
   *  actively used is the clearest "engaged" signal. */
  onFirstSend?: () => void;
}

export function ChatPanel(props: ChatPanelProps) {
  const { runs, collapsed } = props;
  const [messages, setMessages] = useState<Message[]>(() => initialMessages(runs));
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [prefsOpen, setPrefsOpen] = useState(false);
  // Tracks whether the operator has sent at least one message this session.
  // First flip fires `onFirstSend` so the parent can auto-collapse the
  // PromptComposer; subsequent sends are a no-op.
  const firstSentRef = useRef(false);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const inFlight = runs.filter((r) => r.column === 'implementing' || r.column === 'review').length;
  const awaiting = runs.filter((r) => r.column === 'pr').length;

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages.length]);

  // Cancel any in-flight stream when the panel unmounts.
  useEffect(() => () => {
    if (abortRef.current) abortRef.current.abort();
  }, []);

  const send = async (): Promise<void> => {
    if (!input.trim() || sending) return;
    const text = input.trim();
    setInput('');
    setSending(true);
    if (!firstSentRef.current) {
      firstSentRef.current = true;
      props.onFirstSend?.();
    }

    const userMsg: Message = { kind: 'user', text };
    // Take a snapshot of the prior history (excluding system + tool rows)
    // for the wire payload. The server only knows about user/assistant
    // turns; tool events are scoped to a single response.
    let history: Message[] = [];
    setMessages((prev) => {
      history = prev;
      return [...prev, userMsg];
    });

    const wire: WireChatMessage[] = history
      .filter((m): m is Extract<Message, { kind: 'user' | 'assistant' }> =>
        m.kind === 'user' || m.kind === 'assistant',
      )
      .map((m) => ({ role: m.kind, content: m.text }));
    wire.push({ role: 'user', content: text });

    const ctrl = new AbortController();
    abortRef.current = ctrl;

    try {
      for await (const event of api.streamChat(wire, ctrl.signal)) {
        appendStreamEvent(setMessages, event);
        if (event.event === 'chat_complete') {
          // Best-effort refresh — failure here shouldn't block the chat.
          try { await props.onRefresh(); } catch { /* swallow */ }
        }
      }
    } catch (err) {
      if (ctrl.signal.aborted) return;
      const message = err instanceof ApiError
        ? err.message
        : err instanceof Error ? err.message : String(err);
      setMessages((prev) => [...prev, { kind: 'error', text: message }]);
    } finally {
      setSending(false);
      abortRef.current = null;
    }
  };

  return (
    <aside className={`chat ${collapsed ? 'collapsed' : ''}`}>
      <div className="chat-tab" onClick={props.onToggleCollapsed}>
        <span>{collapsed ? '▸' : '◂'}</span>
        <span className="chat-tab-label">orchestrator</span>
      </div>
      {!collapsed && (
        <>
          <div className="chat-head">
            <div className="conn-pill">
              <span className="conn-dot" />
              <span className="mono">orchestrator</span>
            </div>
            <div className="chat-stats mono">
              {inFlight} in flight · {awaiting} awaiting review
            </div>
            <button
              className="icon-btn"
              title="Session defaults — provider, reviewer, repo, budget, bulk-queue gate"
              onClick={() => setPrefsOpen(true)}
              style={{ marginLeft: 'auto' }}
            >
              ⚙
            </button>
          </div>
          {prefsOpen && <ChatPreferencesModal onClose={() => setPrefsOpen(false)} />}
          <div className="chat-body" ref={scrollRef}>
            {messages.map((m, i) => (
              <ChatMessage
                key={i}
                msg={m}
                runs={runs}
                onJumpToCard={props.onJumpToCard}
              />
            ))}
            {sending && <div className="msg-system mono">…thinking</div>}
          </div>
          <div className="chat-input-wrap">
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  void send();
                }
              }}
              placeholder="ask the orchestrator · queue a ticket · cancel runs"
              rows={2}
              disabled={sending}
            />
            <div className="chat-input-foot">
              <span className="kbd">↵ send</span>
              <span className="kbd">⇧↵ newline</span>
              <button
                className="send-btn"
                onClick={() => { void send(); }}
                disabled={sending || !input.trim()}
              >
                {sending ? 'sending…' : 'send'}
              </button>
            </div>
          </div>
        </>
      )}
    </aside>
  );
}

// Translate one wire event into a state mutation. Text deltas append to the
// trailing assistant message of the same turn so multiple text blocks read
// as one paragraph; tool events become their own rows.
function appendStreamEvent(
  setMessages: (fn: (prev: Message[]) => Message[]) => void,
  event: ChatStreamEvent,
): void {
  setMessages((prev) => {
    if (event.event === 'chat_message') {
      const last = prev[prev.length - 1];
      // If the last message is also an assistant turn from this stream,
      // merge — that keeps prose contiguous when the model emits multiple
      // text blocks in one turn.
      if (last && last.kind === 'assistant') {
        return [...prev.slice(0, -1), { ...last, text: last.text + (last.text ? '\n' : '') + event.text }];
      }
      return [...prev, { kind: 'assistant', text: event.text }];
    }
    if (event.event === 'tool_call') {
      const ev = event as Extract<ChatStreamEvent, { event: 'tool_call' }>;
      return [...prev, {
        kind: 'tool_call', tool: ev.tool, args: ev.args, pending: true as const,
        ...(ev.run_id ? { runIdHint: ev.run_id } : {}),
      }];
    }
    if (event.event === 'tool_result') {
      const ev = event as Extract<ChatStreamEvent, { event: 'tool_result' }>;
      // Replace the most recent matching tool_call with a tool_result so
      // the spinner row collapses into a result row.
      const idx = findLastIndex(prev, (m) => m.kind === 'tool_call' && m.tool === ev.tool);
      const result: Message = {
        kind: 'tool_result',
        tool: ev.tool,
        result: ev.data,
        ...(ev.error ? { error: ev.error } : {}),
        ...(ev.run_id ? { runIdHint: ev.run_id } : {}),
      };
      if (idx >= 0) {
        return [...prev.slice(0, idx), result, ...prev.slice(idx + 1)];
      }
      return [...prev, result];
    }
    if (event.event === 'chat_error') {
      return [...prev, { kind: 'error', text: event.error }];
    }
    // chat_complete: no message added; the assistant text already streamed.
    return prev;
  });
}

function findLastIndex<T>(arr: T[], pred: (v: T) => boolean): number {
  for (let i = arr.length - 1; i >= 0; i--) {
    if (pred(arr[i]!)) return i;
  }
  return -1;
}

function ChatMessage(props: {
  msg: Message;
  runs: RunVM[];
  onJumpToCard: (runId: string) => void;
}) {
  const { msg, runs } = props;
  if (msg.kind === 'system') {
    return <div className="msg-system mono">{msg.text}</div>;
  }
  if (msg.kind === 'user') {
    return (
      <div className="msg msg-user">
        <div className="msg-label mono">you</div>
        <div className="msg-text">{msg.text}</div>
      </div>
    );
  }
  if (msg.kind === 'error') {
    return (
      <div className="msg msg-asst">
        <div className="msg-label mono">error</div>
        <div className="msg-text" style={{ color: 'var(--accent-warn, #d97706)' }}>{msg.text}</div>
      </div>
    );
  }
  if (msg.kind === 'tool_call') {
    return (
      <div className="msg msg-tool mono" style={{ opacity: 0.75 }}>
        <span className="msg-label">→ {msg.tool}</span>
        <code style={{ fontSize: 11, marginLeft: 6 }}>
          {summarizeArgs(msg.args)}
        </code>
        <span style={{ marginLeft: 6 }}>…</span>
      </div>
    );
  }
  if (msg.kind === 'tool_result') {
    const card = msg.runIdHint
      ? runs.find((r) => r.runId === msg.runIdHint)
      : null;
    return (
      <div className="msg msg-tool mono">
        <span className="msg-label">← {msg.tool}</span>
        <span style={{ marginLeft: 6, fontSize: 11 }}>
          {msg.error ? `error: ${msg.error}` : summarizeResult(msg.tool, msg.result)}
        </span>
        {card && (
          <button className="inline-card" onClick={() => props.onJumpToCard(card.runId)} style={{ marginTop: 4 }}>
            <span className="ticket-id">[{card.id}]</span>
            <span className="inline-card-title">{card.title}</span>
            <span className={`inline-card-col col-${card.column}`}>{card.column}</span>
          </button>
        )}
      </div>
    );
  }
  // assistant
  return (
    <div className="msg msg-asst">
      <div className="msg-label mono">orchestrator</div>
      <div className="msg-text" style={{ whiteSpace: 'pre-wrap' }}>{msg.text}</div>
    </div>
  );
}

function summarizeArgs(args: unknown): string {
  if (!args || typeof args !== 'object') return '';
  const obj = args as Record<string, unknown>;
  const keys = Object.keys(obj);
  if (keys.length === 0) return '()';
  const pairs = keys.slice(0, 3).map((k) => {
    const v = obj[k];
    const s = typeof v === 'string' ? `"${v}"` : JSON.stringify(v);
    return `${k}=${s.length > 40 ? s.slice(0, 40) + '…' : s}`;
  });
  return `(${pairs.join(', ')}${keys.length > 3 ? ', …' : ''})`;
}

function summarizeResult(tool: string, result: unknown): string {
  if (!result || typeof result !== 'object') return String(result ?? '');
  const obj = result as Record<string, unknown>;
  if (tool === 'list_runs') {
    const count = typeof obj['count'] === 'number' ? obj['count'] : 0;
    return `${count} runs`;
  }
  if (tool === 'queue_ticket') {
    return `queued ${obj['ticket_id'] ?? obj['run_id'] ?? '?'}`;
  }
  if (tool === 'cancel_run') {
    return String(obj['status'] ?? 'cancelled');
  }
  if (tool === 'get_run' || tool === 'summarize_run') {
    const status = obj['status'];
    return status ? `status: ${status}` : 'fetched';
  }
  return JSON.stringify(obj).slice(0, 80);
}

function initialMessages(runs: RunVM[]): Message[] {
  const inFlight = runs.filter((r) => r.column === 'implementing' || r.column === 'review').length;
  const awaiting = runs.filter((r) => r.column === 'pr').length;
  return [
    {
      kind: 'system',
      text: `connected · ${inFlight} runs in flight · ${awaiting} awaiting review`,
    },
    {
      kind: 'assistant',
      text: 'orchestrator chat is live. ask me to queue tickets, cancel runs, or summarize what\'s running.',
    },
  ];
}
